import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Printer, Moon } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

const fmt = (n) => new Intl.NumberFormat("ar-SA", { maximumFractionDigits: 2 }).format(n || 0);

// Simple Arabic number-to-words for typical Umrah amounts (up to 99999)
function numToArabicWords(num) {
  if (!num || num <= 0) return "صفر";
  const ones = ["", "واحد", "اثنان", "ثلاثة", "أربعة", "خمسة", "ستة", "سبعة", "ثمانية", "تسعة", "عشرة", "أحد عشر", "اثنا عشر"];
  const tens = ["", "", "عشرون", "ثلاثون", "أربعون", "خمسون", "ستون", "سبعون", "ثمانون", "تسعون"];
  const teens = ["ثلاثة عشر", "أربعة عشر", "خمسة عشر", "ستة عشر", "سبعة عشر", "ثمانية عشر", "تسعة عشر"];
  const n = Math.floor(num);
  const parts = [];
  const thousands = Math.floor(n / 1000);
  const rem = n % 1000;
  if (thousands) {
    if (thousands === 1) parts.push("ألف");
    else if (thousands === 2) parts.push("ألفان");
    else if (thousands <= 10) parts.push(`${ones[thousands]} آلاف`);
    else parts.push(`${thousands} ألف`);
  }
  if (rem) {
    const hundreds = Math.floor(rem / 100);
    const r2 = rem % 100;
    if (hundreds) {
      if (hundreds === 1) parts.push("مائة");
      else if (hundreds === 2) parts.push("مائتان");
      else parts.push(`${ones[hundreds]}مائة`);
    }
    if (r2) {
      if (r2 <= 12) parts.push(ones[r2]);
      else if (r2 < 20) parts.push(teens[r2 - 13]);
      else {
        const t = Math.floor(r2 / 10);
        const o = r2 % 10;
        if (o === 0) parts.push(tens[t]);
        else parts.push(`${ones[o]} و${tens[t]}`);
      }
    }
  }
  return parts.join(" و") + " ريال سعودي فقط لا غير";
}

export default function VoucherPrint() {
  const { id } = useParams();
  const [ben, setBen] = useState(null);
  const [campaign, setCampaign] = useState(null);
  const [settings, setSettings] = useState({});

  useEffect(() => {
    (async () => {
      const b = await api.post(`/beneficiaries/${id}/voucher`).then((r) => r.data);
      setBen(b);
      const s = await api.get("/settings").then((r) => r.data);
      setSettings(s);
      if (b.campaign_id) {
        const cs = await api.get("/campaigns", { params: { portal: "commercial" } }).then((r) => r.data);
        setCampaign(cs.find((c) => c.id === b.campaign_id));
      }
    })();
  }, [id]);

  if (!ben) return <div className="p-10 text-center text-slate-500">جارٍ التحميل...</div>;

  const payLabel = ben.payment_method === "transfer" ? "تحويل بنكي" : ben.payment_method === "cash" ? "نقدي" : "—";
  const dateStr = new Date(ben.created_at).toLocaleDateString("ar-SA");

  return (
    <div className="min-h-screen bg-white p-8 max-w-3xl mx-auto text-emerald-950" dir="rtl">
      <div className="flex justify-end mb-6 no-print">
        <Button onClick={() => window.print()} className="bg-emerald-900 hover:bg-emerald-800 text-white">
          <Printer className="h-4 w-4 ml-2" /> طباعة السند
        </Button>
      </div>

      <div className="border-4 border-double border-emerald-900 p-8 rounded-lg bg-gradient-to-b from-white to-emerald-50/30">
        <header className="flex items-center justify-between border-b-2 border-emerald-900 pb-4 mb-6">
          <div className="flex items-center gap-4">
            {settings.logo_base64 ? (
              <img src={settings.logo_base64} alt="" className="h-20 w-20 rounded-xl object-cover" />
            ) : (
              <div className="h-20 w-20 rounded-xl bg-emerald-900 flex items-center justify-center">
                <Moon className="h-10 w-10 text-amber-400" />
              </div>
            )}
            <div>
              <h1 className="font-heading text-2xl font-black">{settings.association_name || "جمعية العمرة"}</h1>
              <p className="text-slate-600 text-sm">وثيقة رسمية — سند قبض</p>
            </div>
          </div>
          <div className="text-left">
            <div className="text-xs text-slate-500 mb-1">رقم السند</div>
            <div className="font-heading text-3xl font-black text-amber-700 tabular-nums" data-testid="voucher-number">
              #{ben.voucher_number}
            </div>
          </div>
        </header>

        <h2 className="text-center font-heading text-3xl font-black text-emerald-900 mb-8">سَـنَـد قَـبْـض</h2>

        <div className="space-y-4 text-lg">
          <VoucherRow label="استلمنا من السيّد/ة" value={ben.name} />
          <VoucherRow label="مبلغاً وقدره" value={`${fmt(ben.amount)} ر.س`} bold />
          <VoucherRow label="وذلك بمبلغ (كتابة)" value={numToArabicWords(ben.amount)} italic />
          <VoucherRow label="طريقة الدفع" value={payLabel} />
          <VoucherRow label="اسم الحملة" value={campaign ? `#${campaign.number} ${campaign.name}` : "—"} />
          <VoucherRow label="التاريخ" value={dateStr} />
        </div>

        <div className="grid grid-cols-2 gap-16 mt-16 pt-6 border-t border-slate-300">
          <div className="text-center">
            <div className="border-t-2 border-emerald-900 mt-14 pt-2 text-slate-700 font-bold">توقيع المستلم</div>
          </div>
          <div className="text-center">
            <div className="border-t-2 border-emerald-900 mt-14 pt-2 text-slate-700 font-bold">ختم وتوقيع الجمعية</div>
          </div>
        </div>

        <p className="text-center text-xs text-slate-400 mt-8">
          هذا السند وثيقة رسمية بإصدار {settings.association_name}. رقم مرجعي: {ben.voucher_number}
        </p>
      </div>
    </div>
  );
}

function VoucherRow({ label, value, bold, italic }) {
  return (
    <div className="flex items-baseline gap-3 pb-2 border-b border-dotted border-emerald-900/20">
      <span className="text-slate-700 flex-shrink-0 min-w-[160px]">{label}:</span>
      <span className={`text-emerald-950 flex-1 ${bold ? "font-black text-xl" : "font-bold"} ${italic ? "italic text-base" : ""}`}>
        {value}
      </span>
    </div>
  );
}
